import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import {
  UserSearchResult,
  UserSearchRequest,
} from '../models/user-search-request.model';
import { UserModel } from '../models/user.model';
import { Subject } from 'rxjs';
import { firstValueFrom } from 'rxjs';

/**
 * This service manages the Users in the camera application database
 */
@Injectable({
  providedIn: 'root',
})
export class UserService {
  // Events
  private usersRefreshedSource = new Subject<UserModel[]>();
  public usersRefreshedEvent = this.usersRefreshedSource.asObservable();

  // Variables
  users: UserModel[] = [];
  usersWithCountry: UserModel[] = [];

  constructor(private http: HttpClient) { }

  /**
   * Get Users without countries information
   * @param  {boolean} includeSites - Include Sites
   * @returns {Promise<UserModel[]>} Promise
   */
  async getUsers(includeSites: boolean = false): Promise<UserModel[]> {
    return new Promise<UserModel[]>((resolve, reject) => {
      this.http
        .get<UserModel[]>(
          `${environment.managementApi}/Users/All?includeSites=${includeSites}`
        )
        .subscribe({
          next: (users) => {
            resolve(users);
          },
          error: (err) => {
            reject(err);
          }
        });
    });
  }

  /**
   * Get users with countries information
   * @param  {boolean} includeSites - Include Sites
   * @param  {boolean} includeCountry - Include Countries
   * @returns {Promise<UserModel[]>} Promise
   */
  async getUsersWithCountries(
    includeSites: boolean = false,
    includeCountry: boolean = false
  ): Promise<UserModel[]> {
    return new Promise<UserModel[]>((resolve, reject) => {
      this.http
        .get<UserModel[]>(
          `${environment.managementApi}/Users/All?includeSites=${includeSites}&includeCountry=${includeCountry}`
        )
        .subscribe({
          next: (users) => {
            resolve(users);
          },
          error: (err) => {
            reject(err);
          }
        });
    });
  }

  /**
   * Refresh the users
   * @returns {Promise<void>} Promise
   */
  async refreshUsers(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.getUsers(true)
        .then((users) => {

          this.users = users;
          resolve();
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  /**
   * Get a user by ID
   * @param  {number} userId - User ID
   * @returns {Promise<UserModel>} Promise
   */
  async getUser(userId: number): Promise<UserModel> {
    return new Promise<UserModel>((resolve, reject) => {
      this.http
        .get<UserModel>(
          `${environment.managementApi}/Users/${userId}?includeSites=true&includeCountry=true`
        )
        .subscribe({
          next: (user) => resolve(user),
          error: (err) => reject(err)
        });
    });
  }

  /**
   * Search for a user in Active Directory
   * @param  {UserSearchRequest} request - Search request parameters
   * @returns {Promise<UserSearchResult[]>} Promise
   */
  async searchForADUser(
    request: UserSearchRequest
  ): Promise<UserSearchResult[]> {
    const response = await firstValueFrom(
      this.http.post<UserSearchResult[]>(
        `${environment.managementApi}/ad/search`,
        request,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      )
    );

    return response;
  }

  /**
   * Update a user in the database
   * @param  {UserModel} model - User model to update
   * @returns {Promise<UserModel>} Promise
   */
  async updateUser(model: UserModel): Promise<UserModel> {
    return new Promise<UserModel>((resolve, reject) => {
      this.http
        .put<UserModel>(`${environment.managementApi}/users/update`, model, {
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .subscribe({
          next: (user) => {
            let index = this.users.findIndex((u) => u.Id == user.Id);
            this.users[index] = user;
            resolve(user);
          },
          error: (err) => {
            reject(err);
          }
        });
    });
  }

  /**
   * Adds a user to the database
   * @param {UserModel} model
   * @returns {Promise<UserModel>} Promise
   */
  async addUser(model: UserModel): Promise<UserModel> {
    return new Promise<UserModel>((resolve, reject) => {
      this.http
        .post<UserModel>(`${environment.managementApi}/users/add`, model, {
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .subscribe({
          next: (user) => {
            this.users.push(user);
            resolve(user);
          },
          error: (err) => {
            reject(err);
          }
        });
    });
  }

  /**
   * Remove a user from the database
   * @param  {number} id - User ID
   * @returns {Promise<void>} Promise
   */
  async removeUser(id: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.http
        .delete(`${environment.managementApi}/users/${id}/delete`)
        .subscribe({
          next: () => {
            this.users = this.users.filter((u) => u.Id != id);
            resolve();
          },
          error: (err) => reject(err)
        });
    });
  }
}
